"use client";
import ReorderGroup, { ReorderItemType } from "@/components/ui/ReorderGroup";
import { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { ConfirmOverlay } from "@/components/ui/ConfirmOverlay";
import { AnimatePresence } from "framer-motion";

type EditableReorderListProps<T> = {
  initialItems: T[];
  renderItem: (
    item: ReorderItemType<T>,
    index: number,
    updateItem: (value: T) => void,
  ) => React.ReactNode;
  newItemTemplate: T;
  onChange?: (values: T[]) => void;
  handlePosition?: "top" | "right";
  confirmDeleteMessage?: string;
};

export default function EditableReorderList<T>({
  initialItems,
  renderItem,
  newItemTemplate,
  onChange,
  handlePosition,
  confirmDeleteMessage,
}: EditableReorderListProps<T>) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [items, setItems] = useState<ReorderItemType<T>[]>(() =>
    initialItems.map((val) => ({ id: uuidv4(), ...val })),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setSelectedId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // External form resets and the other language's edits update this list
  // without publishing stale local values back to the form on mount.
  useEffect(() => {
    setItems((previous) => reconcileReorderItems(previous, initialItems));
  }, [initialItems]);

  const commit = (next: ReorderItemType<T>[]) => {
    setItems(next);
    onChange?.(next.map(rowValue));
  };

  const addItem = () => {
    commit([...items, { ...structuredClone(newItemTemplate), id: uuidv4() }]);
  };

  const removeSelected = () => {
    if (selectedId === null) return;
    if (confirmDeleteMessage) {
      setConfirmDeleteId(selectedId);
      return;
    }
    commit(items.filter((item) => item.id !== selectedId));
    setSelectedId(null);
  };

  const updateItem = (id: string, value: T) => {
    commit(items.map((item) => (item.id === id ? { ...value, id } : item)));
  };

  return (
    <div ref={wrapperRef} className="flex flex-col">
      <AnimatePresence>
        {!!confirmDeleteId && (
          <ConfirmOverlay
            message={confirmDeleteMessage}
            title="Confirm Delete"
            variant="danger"
            confirmLabel="Delete"
            onConfirm={() => {
              if (!confirmDeleteId) return;
              commit(items.filter((item) => item.id !== confirmDeleteId));
              setSelectedId(null);
              setConfirmDeleteId(null);
            }}
            onCancel={() => setConfirmDeleteId(null)}
          />
        )}
      </AnimatePresence>
      {/* Control buttons */}
      <div className="sticky top-20 z-10 flex w-fit -translate-y-[34px] items-center gap-x-2 self-end ltr:-translate-x-5 rtl:translate-x-5">
        <button
          type="button"
          aria-label="Add item"
          onClick={addItem}
          className="text-slateCustom flex items-center justify-center rounded-full p-1 text-lg"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <button
          type="button"
          disabled={selectedId === null}
          aria-label="Delete selected item"
          onClick={removeSelected}
          className="text-slateCustom rounded-full p-1 text-lg disabled:opacity-70"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              d="M5 12h14"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <span className="text-gray-300">|</span>
      </div>

      {/* Title and list */}
      <div>
        <ReorderGroup
          handlePosition={handlePosition}
          items={items}
          setItems={commit}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          renderItem={(item, index) =>
            renderItem(item, index, (val: T) => updateItem(item.id, val))
          }
        />
      </div>
    </div>
  );
}

function rowValue<T>(item: ReorderItemType<T>): T {
  const value: Partial<ReorderItemType<T>> = { ...item };
  delete value.id;
  return value as T;
}

function rowUuid(value: unknown): string | undefined {
  return (value as { value?: { uuid?: string } })?.value?.uuid || undefined;
}

export function reconcileReorderItems<T>(
  previous: ReorderItemType<T>[],
  values: T[],
): ReorderItemType<T>[] {
  if (JSON.stringify(previous.map(rowValue)) === JSON.stringify(values))
    return previous;
  const available = new Set(previous.map((item) => item.id));
  const matches = values.map((value) => {
    const uuid = rowUuid(value);
    const match = previous.find(
      (item) =>
        available.has(item.id) &&
        (uuid
          ? rowUuid(item) === uuid
          : JSON.stringify(rowValue(item)) === JSON.stringify(value)),
    );
    if (match) available.delete(match.id);
    return match;
  });
  return values.map((value, index) => {
    const match = matches[index];
    const positional = previous[index];
    const fallback =
      !match && positional && available.has(positional.id)
        ? positional
        : undefined;
    if (fallback) available.delete(fallback.id);
    return { ...value, id: match?.id ?? fallback?.id ?? uuidv4() };
  });
}
